import React, { useEffect, useState } from "react";
import TableHeader from "../../common/MagicPageHeader";
import Header from "../../common/MagicTableHeader";
import MagicTable from "../../common/MagicTable";
import DownloadToXlsxFile from "../../../helper/DownloadToXlsxFile";
import ImageComponent from "../../mui/Image";
import { useFetchStaffTrainingReportsQuery } from "../../../store/apis/reportsApis";

interface TabCategory {
  id: number;
  name: string;
  code: string;
}

interface StaffTrainingTableProps {
  selectedTab: TabCategory | null;
  magicPageHeaderBtn: {
    text: string;
    variant: string;
    startIcon: React.ReactNode;
    onClick: () => void;
    sx: Record<string, string>;
  }[];
  magicTableHeaderBtn: any[];
  params:any;
  isInvalidSearch: boolean;
  onPaginationChange: (pageNo: number) => void;
  onSearch: (searchTerm: string) => void;
}

const StaffTrainingTable: React.FC<StaffTrainingTableProps> = ({
  selectedTab,
  magicTableHeaderBtn,
  params,
  isInvalidSearch,
  onPaginationChange,
  onSearch
}) => {
  const [downloadParams, setDownloadParams] = useState<any>(null);
  const [downloadTrigger, setDownloadTrigger] = useState(false);
  const columns = [
    {
      field: "centreName",
      headerName: "Centre name",
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
      field: "region",
      headerName: "Region",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "funder",
      headerName: "Funder",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "state",
      headerName: "State",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "district",
      headerName: "City",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "cityManager",
      headerName: "City Manager",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "centreStatus",
      headerName: "Centre Status",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "totalStaff",
      headerName: "Total Staff",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "trainingAttended",
      headerName: "Training Attended",
      flex: 1,
      minWidth: 180,
      sortable: true,
    },
    {
      field: "totalNotEligibleStaff",
      headerName: "Total Non eligible staff",
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
        field: "nominatedForTraining",
        headerName: "Nominated for training",
        flex: 1,
        minWidth: 180,
        sortable: true,
      },
      {
        field: "attended",
        headerName: "Attended",
        flex: 1,
        minWidth: 150,
        sortable: true,
      },
      {
        field: "notAttended",
        headerName: "Not attended",
        flex: 1,
        minWidth: 150,
        sortable: true,
      },
      {
        field: "totalTrainedStaff",
        headerName: "Total trained staff",
        flex: 1,
        minWidth: 180,
        sortable: true,
      },
      {
        field: "trainedStaffPercentage",
        headerName: "Trained staff %",
        flex: 1,
        minWidth: 150,
        sortable: true,
      },
      {
        field: "overallStaffTrainedData",
        headerName: "Overall Staff Trained data",
        flex: 1,
        minWidth: 200,
        sortable: true,
      },
      {
        field: "remark",
        headerName: "Remark",
        flex: 1,
        minWidth: 150,
        sortable: true,
      },
  ];

  const {
    data: fetchedReports = {
      reports: [], 
      totalReports: 0, 
    },isLoading
  } = useFetchStaffTrainingReportsQuery(params);
  const { data: downloadData,isLoading: downloadLoading } = useFetchStaffTrainingReportsQuery(downloadParams, {
    skip: !downloadParams,
  });
  const handleDownload = () => {
    const downloadPayload = {
      ...params,
      pageNo: "1",
      pageSize: fetchedReports?.totalReports || "10",
    };
    setDownloadParams(downloadPayload);
    setDownloadTrigger(true); 
  };

  useEffect(() => {
    if (downloadData && downloadTrigger) {
      const sheetData = [
        [
          "Centre Name",
          "Region",
          "Funder",
          "State",
          "City",
          "City Manager",
          "Centre Status",
          "Total Staff",
          "Training Attended",
          "Total Non-eligible Staff",
          "Nominated for Training",
          "Attended",
          "Not Attended",
          "Total Trained Staff",
          "Trained Staff %",
          "Overall Staff Trained Data",
          "Remark",
        ],
        ...(downloadData?.reports.map((data: any) => [
          data.centreName || "-",
          data.region || "-",
          data.funder || "-",
          data.state || "-",
          data.district || "-",
          data.cityManager || "-",
          data.centreStatus || "-",
          data.totalStaff || "-",
          data.trainingAttended || "-",
          data.totalNotEligibleStaff || "-",
          data.nominatedForTraining || "-",
          data.attended || "-",
          data.notAttended || "-",
          data.totalTrainedStaff || "-",
          data.trainedStaffPercentage || "-",
          data.overallStaffTrainedData || "-",
          data.remark || "-",
        ]) || []),
      ];
      DownloadToXlsxFile({
        fileNamePrifix: "Staff-Training-Report",
        sheetData,
      });
      setDownloadTrigger(false);  
    }
    }, [downloadData, downloadTrigger]);  

  const magicPageHeaderBtn = [
    {
      text: "Download",
      variant: "outlined",
      startIcon: (
        <ImageComponent src="/assets/images/login/exportIcon.svg" alt="Export Icon" />
      ),
      onClick: handleDownload,
      loading: downloadLoading,
      sx: {
        background: "white",
        border: "1px solid #D0D5DD",
        borderRadius: "8px",
        color: "black",
      },
    },
  ];
  const totalPages = Math.max(1, Math.ceil((fetchedReports?.totalReports || 0) /10));
  return (
    <>
      <TableHeader
        title={`${selectedTab?.name || "Staff Training"} Report`}
        subTitle="Here you can view the report with all the details."
        buttons={magicPageHeaderBtn}
        chipLabel={`${fetchedReports?.totalReports || 0} entries`}
      />
      <Header
        buttons={magicTableHeaderBtn}
        onSearchfieldChange={(value) => onSearch(String(value))}
        isInvalidSearch={isInvalidSearch}
      />
      <MagicTable
        columns={columns}
        rows={fetchedReports?.reports} 
        loading={isLoading}
        handlePageChange={(event, pageNo) => onPaginationChange(pageNo)}
        currentPage={parseInt(params?.pageNo) || 1}
        count={totalPages}
        rowCellsSx={{ paddingLeft: "16px", textAlign: "start" }}
        headerSx={{
          paddingLeft: "16px",
          textAlign: "start",
          fontWeight: 600,
          color: "#242424",
          padding: "13px 16px",
        }}
        tableSx={{ minHeight: "300px", width: "100%" }}
        sx={{
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#DADADA",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 550,
            fontSize: "14px",
          },
          "& .MuiDataGrid-row": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },}}
      />
    </>
  );
};

export default StaffTrainingTable;